/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXAtomSetter } from '@cloudio-saas/statex';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import NoDataFound from 'api/NoDataFound';
import axios from 'axios';
import clsx from 'clsx';
import { getUrl } from 'config/config';
import Icon from 'icons/Icon';
import { camelCase, snakeCase, upperFirst } from 'lodash-es';
import { usePlatform } from 'platform/PlatformHooks';
import React, { useCallback, useRef, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import useUser, {
  useUserCSVDateFormat,
  useUserCSVDateTimeFormat,
} from 'root/useUser';
import { stopPropagation } from 'utils/events';
import { getURLQueryStringToPost } from 'utils/url';
import WaveDots from 'utils/WaveDots';
import { DSView, sidebarDataSourceWizardTitleAtom } from '../../../atoms';
import DataSourceWizardReview from './DataSourceWizardReview';
import type {
  AcceptFileType,
  Result,
  SuccessResult,
} from './DataSourceWizardTypes';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
    overflow: 'hidden',
    gap: '16px',
  },
  editor: {
    flex: 1,
  },
  form: {
    flexBasis: 'auto',
    flex: 0,
    '& > *': {
      marginTop: 20,
    },
  },
  dropzone: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderWidth: 2,
    borderRadius: 2,
    borderColor: theme.palette.divider,
    borderStyle: 'dashed',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    '&$dropped': {
      borderColor: theme.palette.success.light,
    },
    '&$dragActive': {
      borderColor: theme.palette.success.main,
    },
  },
  dragActive: {},
  dropped: {},
}));

function DataSourceWizardUpload({
  setView,
  appUid,
  roleUid,
  accept,
  width,
}: {
  appUid: string;
  roleUid: string;
  setView: (_view: DSView) => void;
  accept: AcceptFileType;
  width: number;
}): React.ReactElement | null {
  const classes = useStyles();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [tableName, setTableName] = useState('');
  const [result, setResult] = useState<SuccessResult | null>(null);
  const onDrop = useCallback(
    <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[]) => {
      if (fileRejections.length) {
        setFile(null);
        if (fileRejections.length > 1 || acceptedFiles.length) {
          setError('You must upload only a single file!');
        } else {
          const f = fileRejections[0];
          setError(
            `${f.file.name} is rejected! ${f.errors
              .map((e) => e.message)
              .join(', ')}!`,
          );
        }
      } else if (acceptedFiles.length === 1) {
        const f = acceptedFiles[0];
        if (f.size > 200000000) {
          setFile(null);
          setError(`File "${f.name}" is more than 200 MB!`);
        } else {
          let fileName = upperFirst(camelCase(f.name));
          if (accept === 'csv' && fileName.endsWith('CsvZip')) {
            fileName = fileName.substring(0, fileName.length - 6);
          } else {
            setFile(null);
            setError(`File name must end with .${accept}.zip!`);
            return;
          }
          setFile(f);
          setProgress(0);
          setError('');

          if (fileName.length > 40) {
            fileName = fileName.substring(0, 40);
          }
          setName(fileName);
          setTableName(snakeCase(fileName).toUpperCase());
        }
      }
    },
    [accept],
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/zip': ['.zip'] },
    multiple: false,
  });
  const [progress, setProgress] = useState(0);
  const ref = useRef<HTMLInputElement>(null);
  const tref = useRef<HTMLInputElement>(null);
  const platform = usePlatform();
  const user = useUser();
  const setTitle = useStateXAtomSetter(sidebarDataSourceWizardTitleAtom);
  const dateFmt = useUserCSVDateFormat();
  const dateTimeFmt = useUserCSVDateTimeFormat();

  if (result) {
    return (
      <DataSourceWizardReview
        setView={setView}
        appUid={appUid}
        roleUid={roleUid}
        result={result}
        accept={accept}
        width={width}
        back={() => {
          setResult(null);
          setFile(null);
          setProgress(0);
        }}
      />
    );
  }

  const fileType =
    accept === 'csv' ? 'a CSV file (zipped as .csv.zip)' : 'an Excel file';
  let message: React.ReactNode = error;
  if (!error) {
    if (progress) {
      message =
        progress === 90 ? (
          <>
            File uploaded. Processing <WaveDots active />
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 200,
            }}
          >
            <Box sx={{ flex: 1, mr: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                color="primary"
              />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography
                variant="caption"
                component="span"
                color="text.secondary"
              >{`${Math.round(progress)}%`}</Typography>
            </Box>
          </Box>
        );
    } else if (file) {
      message = `File: ${file.name} of size ${
        Math.round((file.size / 1024) * 100) / 100
      } Kilobytes is ready to be uploaded`;
    } else {
      message = (
        <>
          <Typography>
            {`Drag 'n' drop ${fileType} here, or click here to select one.`}
          </Typography>
          <Alert severity="info" sx={{ textAlign: 'start', m: 1 }}>
            Make sure the column titles doesn&apos;t contain any special
            characters other than A-Z, a-z, 0-9, _ (underscore) or space
          </Alert>
          <Alert
            severity="info"
            sx={{ textAlign: 'start', m: 1 }}
            onClick={stopPropagation}
          >
            Make sure all the date values are in the format '{dateFmt}' and all
            the date time values are in the format '{dateTimeFmt}'. You can
            change these default formats in your developer settings sidebar tab.
            Check this{' '}
            <a
              target="_blank"
              href="https://docs.rs/chrono/0.4.23/chrono/format/strftime/index.html"
              rel="noreferrer"
            >
              link
            </a>{' '}
            for more details on the date format string.
          </Alert>
        </>
      );
    }
  } else {
    message = (
      <>
        <Typography variant="caption" component="p" color="error.main">
          {error}
        </Typography>
        <Typography variant="caption" component="p" color="text.secondary">
          Resolve the above issue and re-upload the file!
        </Typography>
      </>
    );
  }

  return (
    <div className={classes.root}>
      <div
        {...getRootProps()}
        className={clsx(classes.dropzone, {
          [classes.dragActive]: isDragActive,
          [classes.dropped]: !!file,
        })}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <Typography>Drop it now...</Typography>
        ) : (
          <NoDataFound color={error ? 'error' : undefined} msg={message} />
        )}
      </div>
      <Box sx={{ display: 'flex' }}>
        <TextField
          inputProps={{
            maxLength: 40,
          }}
          inputRef={ref}
          variant="standard"
          fullWidth
          label="DataSource Name"
          value={name}
          required
          placeholder="Give a name to the DataSource"
          onChange={(e) => {
            const datasourceUid = e.target.value.endsWith(' ')
              ? e.target.value
              : upperFirst(camelCase(e.target.value));
            setName(datasourceUid);
          }}
        />
        <TextField
          sx={{ marginLeft: 2 }}
          inputProps={{
            maxLength: 40,
          }}
          inputRef={tref}
          variant="standard"
          fullWidth
          label="Table Name"
          value={tableName}
          required
          placeholder="Give a name to the DataSource"
          onChange={(e) => {
            const tName =
              e.target.value.endsWith(' ') || e.target.value.endsWith('_')
                ? e.target.value
                : snakeCase(e.target.value.toLowerCase()).toUpperCase();
            setTableName(tName);
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          size="medium"
          fullWidth={false}
          variant="contained"
          data-testid={'upload-csv-button'}
          disabled={!file || !!error || !name || !!progress}
          startIcon={
            progress ? (
              <WaveDots active />
            ) : (
              <Icon iconName="cloud-upload" parentColor="primary" />
            )
          }
          onClick={() => {
            if (!name) {
              ref.current?.focus();
              return;
            }
            if (!file) {
              platform.showWarning('You must select the file first!');
              return;
            }
            const formData = new FormData();
            formData.append('file', file);
            formData.append('datasourceUid', name);
            formData.append('tableName', tableName);
            setProgress(0.001);
            axios({
              url: `${getUrl()}upload/ds_${accept}?${getURLQueryStringToPost(
                user,
              )}`,
              method: 'POST',
              withCredentials: true,
              headers: {
                'X-Application': appUid,
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              data: formData,
              onUploadProgress: (progressEvent) => {
                setProgress(
                  (progressEvent.loaded / (progressEvent.total ?? 100)) * 90,
                );
              },
            })
              .then(({ data }) => {
                const _data = data as Result;
                if (_data.status === 'ERROR') {
                  setProgress(0);
                  setFile(null);
                  setError(_data.message);
                } else {
                  setProgress(100);
                  setFile(null);
                  setError('');
                  setResult(_data);
                  setTitle('Review the fields');
                }
              })
              .catch((e) => {
                setProgress(0);
                setFile(null);
                setError(e.response?.data?.message ?? e.message);
              });
          }}
        >
          Upload
        </Button>
      </Box>
    </div>
  );
}

export default DataSourceWizardUpload;
